import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import axios from "axios";
import { Play, Plus, Trash2, Edit, LogOut, Film, Clapperboard, Users, X, Camera, Link2 } from "lucide-react";
import ShareLinks from "@/pages/admin/ShareLinks";

const SPORTS = ["Pickleball", "Padel", "Cricket", "Football", "Badminton"];
const VIDEO_TYPES = [
  { value: "raw_footage", label: "Raw Footage" },
  { value: "short_clip", label: "Short Clip" },
];

const MOMENT_MAP = {
  Pickleball: ["Smash", "Dink", "Volley", "Rally", "Ace"],
  Padel: ["Smash", "Bandeja", "Vibora", "Rally", "Ace"],
  Cricket: ["Six", "Four", "Wicket", "Catch", "Run Out"],
  Football: ["Goal", "Save", "Tackle", "Free Kick", "Penalty"],
  Badminton: ["Smash", "Drop Shot", "Rally", "Net Shot", "Ace"],
};

// Derivations (kept consistent with VideoCard.js)
const getGameId = (url = "") => {
  const m = url.match(/\/full-replays\/(\d+)\//);
  return m ? m[1] : null;
};
const getPlayMomentId = (url = "") => {
  try {
    const path = new URL(url).pathname.split("/").filter(Boolean);
    if (path.length >= 2) return path[1];
  } catch { /* noop */ }
  return null;
};
const getCameraIdFromUrl = (url = "") => {
  // raw footage: /full-replays/<game_id>/<camera_id>/
  const m = url.match(/\/full-replays\/\d+\/(\d+)\//);
  if (m) return m[1];
  // clip: /<video_id>/<play_moment_id>/...
  try {
    const path = new URL(url).pathname.split("/").filter(Boolean);
    if (path.length) return path[0];
  } catch { /* noop */ }
  return null;
};

function VideoForm({ video, onSubmit, onCancel }) {
  const [form, setForm] = useState({
    title: video?.title || "",
    description: video?.description || "",
    sport: video?.sport || "",
    video_type: video?.video_type || "raw_footage",
    sporting_moment: video?.sporting_moment || "",
    thumbnail_url: video?.thumbnail_url || "",
    angles: video?.angles || [{ label: "Main Camera", url: "" }],
  });

  const addAngle = () => setForm({ ...form, angles: [...form.angles, { label: "", url: "" }] });
  const removeAngle = (idx) => setForm({ ...form, angles: form.angles.filter((_, i) => i !== idx) });
  const updateAngle = (idx, field, value) => {
    const updated = [...form.angles];
    updated[idx] = { ...updated[idx], [field]: value };
    setForm({ ...form, angles: updated });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.title || !form.sport || !form.angles[0]?.url) {
      toast.error("Title, sport, and at least one video URL are required");
      return;
    }
    onSubmit(form);
  };

  const moments = form.sport ? MOMENT_MAP[form.sport] || [] : [];

  return (
    <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-xs font-bold text-[#0A1128]/60 uppercase tracking-wider">Title *</Label>
          <Input data-testid="video-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Video title" className="h-10 bg-[#EAF0EC]/50 border-[#0A1128]/10" />
        </div>
        <div className="space-y-2">
          <Label className="text-xs font-bold text-[#0A1128]/60 uppercase tracking-wider">Sport *</Label>
          <Select value={form.sport} onValueChange={(v) => setForm({ ...form, sport: v, sporting_moment: "" })}>
            <SelectTrigger data-testid="video-sport-select" className="h-10 bg-[#EAF0EC]/50 border-[#0A1128]/10"><SelectValue placeholder="Select sport" /></SelectTrigger>
            <SelectContent>{SPORTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-xs font-bold text-[#0A1128]/60 uppercase tracking-wider">Type *</Label>
          <Select value={form.video_type} onValueChange={(v) => setForm({ ...form, video_type: v })}>
            <SelectTrigger data-testid="video-type-select" className="h-10 bg-[#EAF0EC]/50 border-[#0A1128]/10"><SelectValue /></SelectTrigger>
            <SelectContent>{VIDEO_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {form.video_type === "short_clip" && moments.length > 0 && (
          <div className="space-y-2">
            <Label className="text-xs font-bold text-[#0A1128]/60 uppercase tracking-wider">Sporting Moment</Label>
            <Select value={form.sporting_moment} onValueChange={(v) => setForm({ ...form, sporting_moment: v })}>
              <SelectTrigger data-testid="video-moment-select" className="h-10 bg-[#EAF0EC]/50 border-[#0A1128]/10"><SelectValue placeholder="Select moment" /></SelectTrigger>
              <SelectContent>{moments.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
      </div>

      <div className="space-y-2">
        <Label className="text-xs font-bold text-[#0A1128]/60 uppercase tracking-wider">Description</Label>
        <Textarea data-testid="video-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Optional description" className="bg-[#EAF0EC]/50 border-[#0A1128]/10 min-h-[80px]" />
      </div>

      <div className="space-y-2">
        <Label className="text-xs font-bold text-[#0A1128]/60 uppercase tracking-wider">Thumbnail URL</Label>
        <Input data-testid="video-thumbnail-input" value={form.thumbnail_url} onChange={(e) => setForm({ ...form, thumbnail_url: e.target.value })} placeholder="https://example.com/thumb.jpg" className="h-10 bg-[#EAF0EC]/50 border-[#0A1128]/10" />
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-xs font-bold text-[#0A1128]/60 uppercase tracking-wider flex items-center gap-2">
            <Camera className="w-3 h-3" /> Camera Angles *
          </Label>
          <Button type="button" onClick={addAngle} data-testid="add-angle-btn" variant="outline" size="sm" className="text-xs">
            <Plus className="w-3 h-3 mr-1" /> Add Angle
          </Button>
        </div>
        {form.angles.map((angle, idx) => (
          <div key={idx} className="flex gap-2 items-start" data-testid={`angle-row-${idx}`}>
            <Input value={angle.label} onChange={(e) => updateAngle(idx, "label", e.target.value)} placeholder="Camera label (e.g. Main, Side, Top)" className="h-10 bg-[#EAF0EC]/50 border-[#0A1128]/10 w-1/3" data-testid={`angle-label-input-${idx}`} />
            <Input value={angle.url} onChange={(e) => updateAngle(idx, "url", e.target.value)} placeholder="Video URL" className="h-10 bg-[#EAF0EC]/50 border-[#0A1128]/10 flex-1" data-testid={`angle-url-input-${idx}`} />
            {form.angles.length > 1 && (
              <Button type="button" onClick={() => removeAngle(idx)} variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50 h-10 w-10 p-0" data-testid={`remove-angle-btn-${idx}`}>
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
        ))}
      </div>

      <div className="flex gap-3 pt-2">
        <Button type="submit" data-testid="video-form-submit-btn" className="bg-[#FF5A00] hover:bg-[#E04F00] text-white font-bold px-6">
          {video ? "Update Video" : "Add Video"}
        </Button>
        {onCancel && <Button type="button" variant="outline" onClick={onCancel} data-testid="video-form-cancel-btn">Cancel</Button>}
      </div>
    </form>
  );
}

// ------------- Tables -------------

function RawFootageTable({ videos, onEdit, onDelete }) {
  return (
    <Table>
      <TableHeader>
        <TableRow className="bg-[#EAF0EC]/50">
          <TableHead className="font-bold text-[#0A1128]/70 text-xs uppercase tracking-wider">Game ID</TableHead>
          <TableHead className="font-bold text-[#0A1128]/70 text-xs uppercase tracking-wider">Sport</TableHead>
          <TableHead className="font-bold text-[#0A1128]/70 text-xs uppercase tracking-wider">Camera IDs</TableHead>
          <TableHead className="font-bold text-[#0A1128]/70 text-xs uppercase tracking-wider">Angles</TableHead>
          <TableHead className="font-bold text-[#0A1128]/70 text-xs uppercase tracking-wider text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {videos.length === 0 ? (
          <TableRow><TableCell colSpan={5} className="text-center py-10 text-[#0A1128]/40">No raw footage yet.</TableCell></TableRow>
        ) : videos.map((v) => {
          const firstUrl = v.angles?.[0]?.url || "";
          const gameId = getGameId(firstUrl);
          const cameraIds = (v.angles || []).map((a) => getCameraIdFromUrl(a.url)).filter(Boolean);
          return (
            <TableRow key={v.id} data-testid={`raw-row-${v.id}`}>
              <TableCell className="font-mono text-sm text-[#0A1128]">#{gameId || "—"}</TableCell>
              <TableCell><span className="text-xs font-bold uppercase tracking-wider text-[#0A1128]/60">{v.sport}</span></TableCell>
              <TableCell className="text-xs text-[#0A1128]/70">
                <div className="flex flex-wrap gap-1">
                  {cameraIds.length === 0 ? <span className="text-[#0A1128]/30">—</span> :
                    cameraIds.map((cid, i) => (
                      <span key={i} className="inline-flex items-center gap-1 bg-[#0A1128]/5 px-2 py-0.5 rounded-full font-mono">
                        <Camera className="w-3 h-3" />{cid}
                      </span>
                    ))
                  }
                </div>
              </TableCell>
              <TableCell className="text-sm text-[#0A1128]/60">{v.angles?.length || 0}</TableCell>
              <TableCell className="text-right">
                <div className="flex items-center gap-1 justify-end">
                  <Button onClick={() => onEdit(v)} variant="ghost" size="sm" className="text-[#0A1128]/50 hover:text-[#FF5A00]" data-testid={`edit-raw-btn-${v.id}`}><Edit className="w-4 h-4" /></Button>
                  <Button onClick={() => onDelete(v.id)} variant="ghost" size="sm" className="text-[#0A1128]/50 hover:text-red-500" data-testid={`delete-raw-btn-${v.id}`}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

function ShortClipsTable({ videos, onEdit, onDelete }) {
  return (
    <Table>
      <TableHeader>
        <TableRow className="bg-[#EAF0EC]/50">
          <TableHead className="font-bold text-[#0A1128]/70 text-xs uppercase tracking-wider">Play Moment ID</TableHead>
          <TableHead className="font-bold text-[#0A1128]/70 text-xs uppercase tracking-wider">Sport</TableHead>
          <TableHead className="font-bold text-[#0A1128]/70 text-xs uppercase tracking-wider">Moment</TableHead>
          <TableHead className="font-bold text-[#0A1128]/70 text-xs uppercase tracking-wider">Angles</TableHead>
          <TableHead className="font-bold text-[#0A1128]/70 text-xs uppercase tracking-wider text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {videos.length === 0 ? (
          <TableRow><TableCell colSpan={5} className="text-center py-10 text-[#0A1128]/40">No short clips yet.</TableCell></TableRow>
        ) : videos.map((v) => {
          const firstUrl = v.angles?.[0]?.url || "";
          const momentId = getPlayMomentId(firstUrl);
          return (
            <TableRow key={v.id} data-testid={`clip-row-${v.id}`}>
              <TableCell className="font-mono text-sm text-[#0A1128]">#{momentId || "—"}</TableCell>
              <TableCell><span className="text-xs font-bold uppercase tracking-wider text-[#0A1128]/60">{v.sport}</span></TableCell>
              <TableCell className="text-sm text-[#FF5A00] italic">{v.sporting_moment || "—"}</TableCell>
              <TableCell className="text-sm text-[#0A1128]/60">{v.angles?.length || 0}</TableCell>
              <TableCell className="text-right">
                <div className="flex items-center gap-1 justify-end">
                  <Button onClick={() => onEdit(v)} variant="ghost" size="sm" className="text-[#0A1128]/50 hover:text-[#FF5A00]" data-testid={`edit-clip-btn-${v.id}`}><Edit className="w-4 h-4" /></Button>
                  <Button onClick={() => onDelete(v.id)} variant="ghost" size="sm" className="text-[#0A1128]/50 hover:text-red-500" data-testid={`delete-clip-btn-${v.id}`}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

// ------------- Admin Panel -------------

export default function AdminPanel() {
  const { admin, logoutAdmin, API } = useAuth();
  const navigate = useNavigate();
  const [videos, setVideos] = useState([]);
  const [visitors, setVisitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editVideo, setEditVideo] = useState(null);
  const [activeTab, setActiveTab] = useState("raw_footage");

  const getAuthHeader = () => ({ headers: { Authorization: `Bearer ${admin?.token}` } });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [videosRes, visitorsRes] = await Promise.all([
        axios.get(`${API}/videos`, getAuthHeader()),
        axios.get(`${API}/visitors`, getAuthHeader()),
      ]);
      setVideos(videosRes.data);
      setVisitors(visitorsRes.data);
    } catch (err) {
      console.error("Fetch failed", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (formData) => {
    try {
      await axios.post(`${API}/videos`, formData, getAuthHeader());
      toast.success("Video added successfully");
      setShowForm(false);
      fetchData();
    } catch { toast.error("Failed to add video"); }
  };

  const handleUpdate = async (formData) => {
    try {
      await axios.put(`${API}/videos/${editVideo.id}`, formData, getAuthHeader());
      toast.success("Video updated");
      setEditVideo(null);
      fetchData();
    } catch { toast.error("Failed to update video"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this video?")) return;
    try {
      await axios.delete(`${API}/videos/${id}`, getAuthHeader());
      toast.success("Video deleted");
      fetchData();
    } catch { toast.error("Failed to delete video"); }
  };

  const handleDeleteVisitor = async (id) => {
    if (!window.confirm("Delete this visitor record?")) return;
    try {
      await axios.delete(`${API}/visitors/${id}`, getAuthHeader());
      toast.success("Visitor deleted");
      fetchData();
    } catch { toast.error("Failed to delete visitor"); }
  };

  const handleDeleteAllVisitors = async () => {
    if (!window.confirm(`Delete ALL ${visitors.length} visitor records? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/visitors`, getAuthHeader());
      toast.success("All visitor records deleted");
      fetchData();
    } catch { toast.error("Failed to delete visitors"); }
  };

  const handleLogout = () => { logoutAdmin(); navigate("/admin/login"); };

  const rawFootage = videos.filter((v) => v.video_type === "raw_footage");
  const shortClips = videos.filter((v) => v.video_type === "short_clip");

  const tabs = [
    { id: "raw_footage", label: "Raw Footage", icon: Film, count: rawFootage.length },
    { id: "short_clip", label: "Short Clips", icon: Clapperboard, count: shortClips.length },
    { id: "share_links", label: "Share Links", icon: Link2, count: null },
    { id: "visitors", label: "Visitors", icon: Users, count: visitors.length },
  ];

  return (
    <div className="min-h-screen" data-testid="admin-panel">
      <header className="glass-header sticky top-0 z-50 w-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Play className="w-6 h-6 text-[#FF5A00] fill-[#FF5A00]" />
            <span className="text-xl font-bold tracking-tight text-[#0A1128]" style={{ fontFamily: "Outfit" }}>HiFy Admin</span>
          </div>
          <Button onClick={handleLogout} variant="ghost" className="text-[#0A1128]/60 hover:text-[#0A1128]" data-testid="admin-logout-btn">
            <LogOut className="w-4 h-4 mr-2" /> Logout
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-2 mb-8 overflow-x-auto" data-testid="admin-tabs">
          {tabs.map((t) => {
            const Icon = t.icon;
            const active = activeTab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                data-testid={`admin-tab-${t.id}`}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all whitespace-nowrap ${
                  active ? "text-[#FF5A00] bg-[#FF5A00]/10" : "text-[#0A1128]/60 hover:text-[#0A1128] hover:bg-[#0A1128]/5"
                }`}
              >
                <Icon className="w-4 h-4" /> {t.label}{t.count !== null ? ` (${t.count})` : ""}
              </button>
            );
          })}
        </div>

        {(activeTab === "raw_footage" || activeTab === "short_clip") && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-[#0A1128]" style={{ fontFamily: "Outfit" }}>
                {activeTab === "raw_footage" ? "Manage Raw Footage" : "Manage Short Clips"}
              </h2>
              <Button
                onClick={() => { setShowForm(true); setEditVideo({ video_type: activeTab }); }}
                data-testid="add-video-btn"
                className="bg-[#FF5A00] hover:bg-[#E04F00] text-white font-bold"
              >
                <Plus className="w-4 h-4 mr-2" /> Add {activeTab === "raw_footage" ? "Raw Footage" : "Clip"}
              </Button>
            </div>

            {(showForm || (editVideo && editVideo.id)) && (
              <div className="bg-white rounded-xl border border-[#0A1128]/10 p-6 mb-6" data-testid="video-form-container">
                <h3 className="text-lg font-semibold text-[#0A1128] mb-4" style={{ fontFamily: "Outfit" }}>
                  {editVideo?.id ? "Edit Video" : "Add New Video"}
                </h3>
                <VideoForm
                  video={editVideo?.id ? editVideo : { video_type: activeTab }}
                  onSubmit={editVideo?.id ? handleUpdate : handleCreate}
                  onCancel={() => { setShowForm(false); setEditVideo(null); }}
                />
              </div>
            )}

            <div className="bg-white rounded-xl border border-[#0A1128]/10 overflow-hidden">
              {activeTab === "raw_footage" ? (
                <RawFootageTable videos={rawFootage} onEdit={(v) => { setEditVideo(v); setShowForm(false); }} onDelete={handleDelete} />
              ) : (
                <ShortClipsTable videos={shortClips} onEdit={(v) => { setEditVideo(v); setShowForm(false); }} onDelete={handleDelete} />
              )}
            </div>
          </div>
        )}

        {activeTab === "share_links" && (
          <ShareLinks API={API} getAuthHeader={getAuthHeader} />
        )}

        {activeTab === "visitors" && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-[#0A1128]" style={{ fontFamily: "Outfit" }}>Visitor Records</h2>
              {visitors.length > 0 && (
                <Button
                  onClick={handleDeleteAllVisitors}
                  variant="outline"
                  className="text-red-500 border-red-200 hover:bg-red-50"
                  data-testid="delete-all-visitors-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" /> Delete All
                </Button>
              )}
            </div>
            <div className="bg-white rounded-xl border border-[#0A1128]/10 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#EAF0EC]/50">
                    <TableHead className="font-bold text-[#0A1128]/70 text-xs uppercase tracking-wider">Name</TableHead>
                    <TableHead className="font-bold text-[#0A1128]/70 text-xs uppercase tracking-wider">Company</TableHead>
                    <TableHead className="font-bold text-[#0A1128]/70 text-xs uppercase tracking-wider">Email</TableHead>
                    <TableHead className="font-bold text-[#0A1128]/70 text-xs uppercase tracking-wider">Phone</TableHead>
                    <TableHead className="font-bold text-[#0A1128]/70 text-xs uppercase tracking-wider">Date</TableHead>
                    <TableHead className="font-bold text-[#0A1128]/70 text-xs uppercase tracking-wider text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visitors.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-10 text-[#0A1128]/40">No visitors yet.</TableCell></TableRow>
                  ) : visitors.map((v) => (
                    <TableRow key={v.id} data-testid={`visitor-row-${v.id}`}>
                      <TableCell className="font-medium text-[#0A1128] text-sm">{v.name}</TableCell>
                      <TableCell className="text-sm text-[#0A1128]/60">{v.company}</TableCell>
                      <TableCell className="text-sm text-[#0A1128]/60">{v.email}</TableCell>
                      <TableCell className="text-sm text-[#0A1128]/60 font-mono">{v.phone || "—"}</TableCell>
                      <TableCell className="text-sm text-[#0A1128]/40">{new Date(v.created_at).toLocaleDateString()}</TableCell>
                      <TableCell className="text-right">
                        <Button onClick={() => handleDeleteVisitor(v.id)} variant="ghost" size="sm" className="text-[#0A1128]/50 hover:text-red-500" data-testid={`delete-visitor-btn-${v.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
